import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Group, Vector3Tuple } from 'three';
import { BoxGeometry, Euler, Quaternion, Vector3 } from 'three';

import { TOOL_LABELING_BOX } from '@/constant/toolConstant';
import { useUnleash } from '@/package';
import { useLabelingBoxContext } from '@/provider';
import { useEditingLabelBoxStore } from '@/store';
import type { NEW__LabelingBox } from '@/util';
import { COLOR } from '@/util';

import useDefaultViewFrontMove from './hook/useDefaultViewFrontMove';
import useDefaultViewSideMove from './hook/useDefaultViewSideMove';
import useDefaultViewTopMove from './hook/useDefaultViewTopMove';
import useDefaultViewTopMultiMove from './hook/useDefaultViewTopMultiMove';
import { useDefaultViewTopResize } from './hook/useDefaultViewTopResize';
import useDefaultViewTopRotate from './hook/useDefaultViewTopRotate';

const DefaultViewEditingLabelBoxContainer = ({
  disabled,
}: {
  disabled: boolean;
}) => {
  const { selectedLabelingBoxList } = useLabelingBoxContext();
  const [renderedBoxIDList, setRenderedBoxIDList] = useState<string[]>([]);
  const { isEnabled } = useUnleash();
  const isSingleMoveRef = useRef(false);
  const isMultiMoveRef = useRef(false);

  const setRenderBoxListById = (boxIdList: string[]) =>
    setRenderedBoxIDList(boxIdList);

  const renderedBoxList = selectedLabelingBoxList.filter(box =>
    renderedBoxIDList.includes(box.id ?? ''),
  );

  useDefaultViewTopResize({
    setRenderBoxListById,
    disabled: disabled || !isEnabled('vuex.sapporo.enabledResizingForTopView'),
  });

  useDefaultViewTopMove({
    setRenderBoxListById,
    disabled,
    isSingleMoveRef,
    isMultiMoveRef,
  });

  useDefaultViewTopMultiMove({
    setRenderBoxListById,
    disabled,
    isMultiMoveRef,
    isSingleMoveRef,
  });

  useDefaultViewTopRotate({
    setRenderBoxListById,
    disabled,
  });

  useDefaultViewSideMove({
    setRenderBoxListById,
    disabled,
  });

  useDefaultViewFrontMove({
    setRenderBoxListById,
    disabled,
  });

  return renderedBoxList.map((box, index) => {
    return <DefaultViewEditingLabelBox key={box.id ?? index} box={box} />;
  });
};

const DefaultViewEditingLabelBox = ({ box }: { box: NEW__LabelingBox }) => {
  const groupRef = useRef<Group>(null);
  const orgSelectedBoxSize = box?.dimension ?? [0, 0, 0];
  const defaultPosition = useMemo(() => new Vector3(), []);
  const defaultQuaternion = useMemo(() => new Quaternion(), []);
  const orgSelectedBoxPos = box?.boxMesh?.position ?? defaultPosition;
  const orgSelectedBoxQuaternion =
    box?.boxMesh?.quaternion ?? defaultQuaternion;
  const tempQuaternion = useMemo(() => new Quaternion(), []);

  const { moveOffset, rotationX, rotationY, rotationZ, sizeOffset } =
    useEditingLabelBoxStore();

  const calculatedSize = useMemo(
    () => [
      orgSelectedBoxSize[0] + sizeOffset[0],
      orgSelectedBoxSize[1] + sizeOffset[1],
      orgSelectedBoxSize[2] + sizeOffset[2],
    ],
    [orgSelectedBoxSize, sizeOffset],
  );

  const calculatedPosition = [
    orgSelectedBoxPos.x + moveOffset[0],
    orgSelectedBoxPos.y + moveOffset[1],
    orgSelectedBoxPos.z + moveOffset[2],
  ];

  const boxGeometry = useMemo(
    () => new BoxGeometry(...calculatedSize),
    [calculatedSize],
  );

  useEffect(() => {
    return () => {
      boxGeometry.dispose();
    };
  }, [boxGeometry]);

  useLayoutEffect(() => {
    if (groupRef.current) {
      tempQuaternion.copy(orgSelectedBoxQuaternion);

      if (rotationX) {
        groupRef.current.rotation.reorder('YZX');

        const euler = new Euler().setFromQuaternion(tempQuaternion, 'YZX');
        groupRef.current.rotation.x = euler.x + rotationX;
      }
      if (rotationY) {
        groupRef.current.rotation.reorder('XZY');

        const euler = new Euler().setFromQuaternion(tempQuaternion, 'XZY');
        groupRef.current.rotation.y = euler.y - rotationY;
      }
      if (rotationZ) {
        groupRef.current.rotation.reorder('XYZ');
        const euler = new Euler().setFromQuaternion(tempQuaternion, 'XYZ');
        groupRef.current.rotation.z = euler.z + rotationZ;
      }
    }
  }, [rotationX, rotationY, rotationZ]);

  return (
    <group
      ref={groupRef}
      position={
        new Vector3(
          calculatedPosition[0],
          calculatedPosition[1],
          calculatedPosition[2],
        )
      }
      quaternion={orgSelectedBoxQuaternion}
    >
      <mesh>
        <boxGeometry args={calculatedSize as Vector3Tuple} />
        <meshBasicMaterial
          opacity={TOOL_LABELING_BOX.OPACITY.BOX.DEFAULT}
          transparent
          vertexColors
        />
      </mesh>
      <lineSegments>
        <edgesGeometry args={[boxGeometry]} />
        <lineBasicMaterial
          color={COLOR.DEFAULT_LINE}
          opacity={TOOL_LABELING_BOX.OPACITY.LINE.DEFAULT}
          transparent
          linewidth={1}
        />
      </lineSegments>
    </group>
  );
};

DefaultViewEditingLabelBox.displayName = 'DefaultViewEditingLabelBox';
DefaultViewEditingLabelBoxContainer.displayName =
  'DefaultViewEditingLabelBoxContainer';

export default DefaultViewEditingLabelBoxContainer;
